'use client';

import type React from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useAuth } from '../providers/AuthProvider';
import { z } from 'zod';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { useUpdateProfileMutation } from '@/lib/hooks/mutations/use-update-profile-mutation';
import { useRouter } from 'next/navigation';
import { Separator } from '@/components/ui/separator';

interface AuthenticationDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AuthenticationDialog({
  isOpen,
  onClose,
}: AuthenticationDialogProps) {
  const [mode, setMode] = useState<'signIn' | 'signUp'>('signIn');
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='max-w-md w-full p-0 bg-neutral-800 border-neutral-700 text-white'>
        <DialogHeader className='flex flex-row items-center justify-between p-6 pb-4'>
          <DialogTitle className='text-xl font-semibold text-white'>
            {mode === 'signIn' ? 'Sign In' : 'Sign Up'}
          </DialogTitle>
        </DialogHeader>

        <div className='px-6 pb-6 space-y-6'>
          <Separator className='bg-neutral-700' />

          <AuthModeToggle mode={mode} setMode={setMode} />

          {mode === 'signIn' && (
            <>
              <SocialButtons />
              <div className='relative'>
                <Separator className='bg-neutral-700' />
                <div className='absolute inset-0 flex items-center justify-center'>
                  <span className='px-4 bg-neutral-800 text-neutral-400 text-sm'>
                    Or continue with email
                  </span>
                </div>
              </div>
            </>
          )}

          {mode === 'signIn' ? (
            <CredentialsSignInForm onClose={onClose} />
          ) : !signUpSuccess ? (
            <SignUpForm setSignUpSuccess={setSignUpSuccess} />
          ) : (
            <SignUpSuccessMessage
              setSignUpSuccess={setSignUpSuccess}
              setMode={setMode}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Subcomponents ---

function AuthModeToggle({
  mode,
  setMode,
}: {
  mode: 'signIn' | 'signUp';
  setMode: (m: 'signIn' | 'signUp') => void;
}) {
  return (
    <div className='flex gap-2'>
      <Button
        variant={mode === 'signIn' ? 'default' : 'ghost'}
        className={cn(
          'flex-1 h-10 rounded-lg',
          mode === 'signIn'
            ? 'bg-fuchsia-600 hover:bg-fuchsia-700 text-white'
            : 'bg-transparent hover:bg-neutral-700 text-neutral-400 hover:text-white',
        )}
        onMouseDown={() => setMode('signIn')}
      >
        Sign In
      </Button>
      <Button
        variant={mode === 'signUp' ? 'default' : 'ghost'}
        className={cn(
          'flex-1 h-10 rounded-lg',
          mode === 'signUp'
            ? 'bg-fuchsia-600 hover:bg-fuchsia-700 text-white'
            : 'bg-transparent hover:bg-neutral-700 text-neutral-400 hover:text-white',
        )}
        onMouseDown={() => setMode('signUp')}
      >
        Sign Up
      </Button>
    </div>
  );
}

function SocialButtons() {
  const { signInWithGoogle } = useAuth();
  return (
    <div className='space-y-4'>
      <Button
        variant='outline'
        className='w-full h-10 bg-white border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center justify-center rounded-md font-medium'
        onMouseDown={signInWithGoogle}
      >
        <svg width='20' height='20' viewBox='0 0 24 24'>
          <path
            fill='#4285F4'
            d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
          />
          <path
            fill='#34A853'
            d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
          />
          <path
            fill='#FBBC05'
            d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
          />
          <path
            fill='#EA4335'
            d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
          />
        </svg>
        <span>Sign in with Google</span>
      </Button>
    </div>
  );
}

function CredentialsSignInForm({ onClose }: { onClose: () => void }) {
  const { signInWithCredentials } = useAuth();
  const router = useRouter();
  const schema = z.object({
    email: z.string().min(1, 'Email is required'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
  });
  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
    mode: 'onChange',
  });
  const [signInError, setSignInError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setSignInError(null);
    setIsSending(true);
    try {
      const { error } = await signInWithCredentials(data);
      if (error) {
        setSignInError(error.message || 'Sign in failed.');
      } else {
        onClose();
        router.push('/generate');
      }
    } catch (error: any) {
      setSignInError(error?.message || 'Sign in failed.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-neutral-400 text-base'>
                Email
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder='your@email.com'
                  autoComplete='email'
                  className='bg-neutral-700 py-6 border-neutral-600 text-white placeholder:text-neutral-400 focus:border-fuchsia-500'
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-neutral-400 text-base'>
                Password
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type='password'
                  placeholder='Password'
                  autoComplete='current-password'
                  className='bg-neutral-700 py-6 border-neutral-600 text-white placeholder:text-neutral-400 focus:border-fuchsia-500'
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {signInError && (
          <div className='text-red-400 text-sm'>{signInError}</div>
        )}
        <Button
          type='submit'
          className='w-full h-12 mt-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-medium rounded-lg'
          disabled={isSending}
        >
          {isSending ? 'Signing In...' : 'Sign In'}
        </Button>
      </form>
    </Form>
  );
}

function SignUpForm({
  setSignUpSuccess,
}: {
  setSignUpSuccess: (v: boolean) => void;
}) {
  const { signUpWithCredentials } = useAuth();
  const updateProfileMutation = useUpdateProfileMutation();
  const schema = z
    .object({
      username: z.string().min(3, 'Username must be at least 3 characters'),
      email: z.string().email('Invalid email'),
      password: z.string().min(6, 'Password must be at least 6 characters'),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    });
  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setFormError(null);
    setIsSending(true);
    try {
      const { error } = await signUpWithCredentials({
        email: data.email,
        password: data.password,
        username: data.username,
      });
      if (error) {
        setFormError(error.message || 'Sign up failed.');
      } else {
        updateProfileMutation.mutate({ username: data.username });
        setSignUpSuccess(true);
      }
    } catch (error: any) {
      setFormError(error?.message || 'Sign up failed.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
        <FormField
          control={form.control}
          name='username'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-neutral-400 text-base'>
                Username
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder='yourusername'
                  autoComplete='username'
                  className='bg-neutral-700 py-6  border-neutral-600 text-white placeholder:text-neutral-400 focus:border-fuchsia-500'
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-neutral-400 text-base'>
                Email
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder='name@example.com'
                  autoComplete='email'
                  className='bg-neutral-700 py-6 border-neutral-600 text-white placeholder:text-neutral-400 focus:border-fuchsia-500'
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-neutral-400 text-base'>
                Password
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type='password'
                  placeholder='Password'
                  autoComplete='new-password'
                  className='bg-neutral-700 py-6 border-neutral-600 text-white placeholder:text-neutral-400 focus:border-fuchsia-500'
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='confirmPassword'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-neutral-400 text-base'>
                Confirm Password
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type='password'
                  placeholder='Confirm Password'
                  autoComplete='new-password'
                  className='bg-neutral-700 py-6 border-neutral-600 text-white placeholder:text-neutral-400 focus:border-fuchsia-500'
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {formError && <div className='text-red-400 text-sm'>{formError}</div>}
        <Button
          type='submit'
          className='w-full h-12 mt-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-medium rounded-lg'
          disabled={isSending || !form.formState.isValid}
        >
          {isSending ? 'Signing Up...' : 'Sign Up'}
        </Button>
      </form>
    </Form>
  );
}

function SignUpSuccessMessage({
  setSignUpSuccess,
  setMode,
}: {
  setSignUpSuccess: (v: boolean) => void;
  setMode: (m: 'signIn' | 'signUp') => void;
}) {
  return (
    <div className='space-y-6 text-center'>
      <div className='rounded-xl p-6 bg-neutral-700 border border-neutral-600'>
        <div className='flex justify-center mb-3'>
          <div className='rounded-full bg-green-500/20 p-2'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-6 w-6 text-green-400'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
        </div>
        <p className='font-medium text-white'>Sign up successful!</p>
        <p className='text-sm mt-2 text-neutral-400'>
          Please check your email to confirm your account.
        </p>
      </div>
      <Button
        className='w-full h-12 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-medium rounded-full'
        onMouseDown={() => {
          setSignUpSuccess(false);
          setMode('signIn');
        }}
      >
        Back to Sign In
      </Button>
    </div>
  );
}
